import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './AdminSpotsManager.css';

const TYPE_LABELS = { artist: '🎤 Artist', album: '💿 Album', track: '🎵 Track', other: '🔗 Link' };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysLeft(lockedUntil) {
  const diff = new Date(lockedUntil) - Date.now();
  if (diff <= 0) return 'Unlocked';
  return `${Math.ceil(diff / (24 * 60 * 60 * 1000))}d locked`;
}

export default function AdminSpotsManager() {
  const [spots, setSpots]             = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterWallet, setFilterWallet] = useState('');

  // Allocation form
  const [allocWallet, setAllocWallet]   = useState('');
  const [allocCount, setAllocCount]     = useState('');
  const [allocNotes, setAllocNotes]     = useState('');
  const [allocSaving, setAllocSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/spots/admin/all');
      setSpots(res.data.spots || []);
      setAllocations(res.data.allocations || []);
    } catch {
      toast.error('Failed to load spots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSetAllocation = async () => {
    if (!allocWallet.trim()) return toast.error('Enter a wallet address');
    const count = parseInt(allocCount, 10);
    if (isNaN(count) || count < 0) return toast.error('Enter a valid spot count');
    setAllocSaving(true);
    try {
      await api.put('/spots/admin/allocation', {
        walletAddress: allocWallet.trim(),
        totalSpots: count,
        notes: allocNotes.trim(),
      });
      toast.success(`Set ${count} spot(s) for ${allocWallet.trim().slice(0, 10)}…`);
      setAllocWallet(''); setAllocCount(''); setAllocNotes('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to set allocation');
    } finally {
      setAllocSaving(false);
    }
  };

  const handleClearSpot = async (walletAddress, spotIndex) => {
    if (!window.confirm(`Clear spot ${spotIndex + 1} for ${walletAddress}? This unlocks it immediately.`)) return;
    try {
      await api.delete('/spots/admin/spot', { data: { walletAddress, spotIndex } });
      toast.success('Spot cleared');
      load();
    } catch {
      toast.error('Failed to clear spot');
    }
  };

  const filteredSpots = filterWallet.trim()
    ? spots.filter(s => s.walletAddress.includes(filterWallet.trim().toLowerCase()))
    : spots;

  // Group spots by wallet for display
  const byWallet = {};
  filteredSpots.forEach(s => {
    if (!byWallet[s.walletAddress]) byWallet[s.walletAddress] = [];
    byWallet[s.walletAddress].push(s);
  });

  return (
    <div className="asm-root">

      {/* ── Allocations Panel ── */}
      <div className="asm-panel">
        <h3 className="asm-panel-title">Set Spot Allocation</h3>
        <p className="asm-panel-desc">Assign how many spots a wallet address can fill.</p>
        <div className="asm-alloc-form">
          <input
            className="asm-input asm-input-wide"
            placeholder="Wallet address (0x…)"
            value={allocWallet}
            onChange={e => setAllocWallet(e.target.value)}
          />
          <input
            className="asm-input asm-input-short"
            type="number"
            min="0"
            placeholder="# Spots"
            value={allocCount}
            onChange={e => setAllocCount(e.target.value)}
          />
          <input
            className="asm-input asm-input-wide"
            placeholder="Notes (optional)"
            value={allocNotes}
            onChange={e => setAllocNotes(e.target.value)}
          />
          <button
            className="asm-btn asm-btn-primary"
            onClick={handleSetAllocation}
            disabled={allocSaving}
          >
            {allocSaving ? 'Saving…' : 'Save Allocation'}
          </button>
        </div>
      </div>

      {/* ── Allocation List ── */}
      {allocations.length > 0 && (
        <div className="asm-panel">
          <h3 className="asm-panel-title">Current Allocations</h3>
          <div className="asm-alloc-table">
            <div className="asm-alloc-row asm-alloc-header">
              <span>Wallet</span>
              <span>Spots</span>
              <span>Notes</span>
              <span>Updated</span>
            </div>
            {allocations.map(a => (
              <div key={a._id} className="asm-alloc-row" onClick={() => setAllocWallet(a.walletAddress)}>
                <span className="asm-wallet">{a.walletAddress}</span>
                <span className="asm-count">{a.totalSpots}</span>
                <span className="asm-notes">{a.notes || '—'}</span>
                <span className="asm-date">{formatDate(a.updatedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Submitted Links ── */}
      <div className="asm-panel">
        <div className="asm-panel-header">
          <h3 className="asm-panel-title">Submitted Links</h3>
          <input
            className="asm-input asm-search"
            placeholder="Filter by wallet…"
            value={filterWallet}
            onChange={e => setFilterWallet(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="asm-loading">Loading…</div>
        ) : filteredSpots.length === 0 ? (
          <div className="asm-empty">No links submitted yet.</div>
        ) : (
          Object.entries(byWallet).map(([wallet, walletSpots]) => {
            const alloc = allocations.find(a => a.walletAddress === wallet);
            return (
              <div key={wallet} className="asm-wallet-group">
                <div className="asm-wallet-header">
                  <span className="asm-wallet-addr">{wallet}</span>
                  <span className="asm-wallet-meta">{walletSpots.length} / {alloc?.totalSpots ?? '?'} spots used</span>
                </div>
                <div className="asm-spots-list">
                  {walletSpots.map(spot => (
                    <div key={spot._id} className="asm-spot-row">
                      <div className="asm-spot-index">#{spot.spotIndex + 1}</div>
                      <div className="asm-spot-type">{TYPE_LABELS[spot.linkType] || spot.linkType}</div>
                      <a
                        href={spot.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="asm-spot-link"
                      >
                        {spot.link}
                      </a>
                      <div className="asm-spot-dates">
                        <span>Submitted: {formatDate(spot.submittedAt)}</span>
                        <span className={new Date(spot.lockedUntil) > new Date() ? 'asm-locked' : 'asm-unlocked'}>
                          {daysLeft(spot.lockedUntil)}
                        </span>
                      </div>
                      <button
                        className="asm-btn asm-btn-danger asm-btn-sm"
                        onClick={() => handleClearSpot(spot.walletAddress, spot.spotIndex)}
                        title="Clear / unlock this spot"
                      >
                        Clear
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
